import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ReactNode } from 'react';

interface PublicRouteProps {
  children: ReactNode;
}

const PublicRoute = ({ children }: PublicRouteProps) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If not authenticated, allow access to public routes
  if (!isAuthenticated) {
    return <>{children}</>;
  }

  // User is authenticated - check if they should be redirected
  const userRole = user?.type;
  const currentPath = location.pathname;
  
  // Allow access to auth page only in specific cases
  const fromDashboard = location.state?.from?.pathname?.includes('/dashboard');
  const isExplicitAuthNavigate = currentPath === '/auth';
  
  // If user is on auth page and came from dashboard (after logout), allow access
  if (isExplicitAuthNavigate && fromDashboard) {
    return <>{children}</>;
  }
  
  // If user is on auth page but didn't come from dashboard, redirect to their dashboard
  if (isExplicitAuthNavigate && !fromDashboard) {
    let redirectPath: string;

    switch (userRole) {
      case 'admin':
        redirectPath = '/admin/dashboard';
        break;
      case 'client':
        redirectPath = '/dashboard';
        break;
      default:
        // Fallback to client dashboard if role is unknown
        redirectPath = '/dashboard';
        break;
    }

    return <Navigate to={redirectPath} replace />;
  }

  // For all other public pages (home, about, etc.), allow access
  if (!isExplicitAuthNavigate) {
    return <>{children}</>;
  }

  // Default redirect to dashboard
  let redirectPath: string;

  switch (userRole) {
    case 'admin':
      redirectPath = '/admin/dashboard';
      break;
    case 'client':
      redirectPath = '/dashboard';
      break;
    default:
      redirectPath = '/dashboard';
      break;
  }

  return <Navigate to={redirectPath} replace />;
};

export default PublicRoute;
